import { log } from './logger';

const PROPS = ['margin-right', 'overflow-x', 'transform', 'right', 'width', 'max-width'] as const;
const OVERLAY_BREAKPOINT = 900;
const MIN_HOST_WIDTH = 560;

export type LayoutMode = 'push' | 'overlay';

type Strategy = { name: string; apply: (target: HTMLElement, width: number) => void };

const STRATEGIES: Strategy[] = [
  {
    name: 'margin-right',
    apply: (target, width) => target.style.setProperty('margin-right', `${width}px`, 'important'),
  },
  {
    name: 'right-inset',
    apply: (target, width) => target.style.setProperty('right', `${width}px`, 'important'),
  },
  {
    name: 'width-calc',
    apply: (target, width) => {
      target.style.setProperty('width', `calc(100% - ${width}px)`, 'important');
      target.style.setProperty('max-width', `calc(100% - ${width}px)`, 'important');
    },
  },
];

const WRAP_ATTR = 'data-agent-host-wrap';

export class HostLayout {
  private readonly saved = new Map<string, string>();
  private target: HTMLElement | null = null;
  private wrapper: HTMLElement | null = null;
  private applied = false;
  private effective: LayoutMode = 'push';
  private selfDispatched = false;
  private resizeRaf = 0;
  private recheckRaf = 0;
  private contentObserver: ResizeObserver | null = null;
  private last = { open: false, width: 0 };

  constructor(
    private readonly requested: LayoutMode,
    private readonly hostSelector?: string,
    private readonly ownRoot?: () => Element | null,
  ) {
    window.addEventListener('resize', this.onViewportResize, { passive: true });
  }

  private resolveMode(width: number): LayoutMode {
    if (this.requested === 'overlay') return 'overlay';
    if (window.innerWidth < OVERLAY_BREAKPOINT) return 'overlay';
    if (window.innerWidth - width < MIN_HOST_WIDTH) return 'overlay';
    return 'push';
  }

  private candidates(): HTMLElement[] {
    if (this.hostSelector) {
      try {
        const found = document.querySelector<HTMLElement>(this.hostSelector);
        if (found) return [found];
        log.warn(`hostSelector "${this.hostSelector}" matched nothing`);
      } catch {
        log.warn(`hostSelector "${this.hostSelector}" is not a valid selector`);
      }
    }

    const own = this.ownRoot?.() ?? null;
    const shells: HTMLElement[] = [];

    for (const child of Array.from(document.body?.children ?? [])) {
      if (!(child instanceof HTMLElement)) continue;
      if (own && (child === own || child.contains(own))) continue;
      const rect = child.getBoundingClientRect();
      if (rect.height === 0) continue;
      if (rect.width >= window.innerWidth - 2) shells.push(child);
    }

    return [...shells, document.body, document.documentElement].filter(Boolean) as HTMLElement[];
  }

  private collectPinned(): HTMLElement[] {
    const own = this.ownRoot?.() ?? null;
    const out: HTMLElement[] = [];
    const all = document.body?.querySelectorAll('*') ?? [];
    let scanned = 0;
    for (const el of Array.from(all)) {
      if (scanned++ > 4000) break;
      if (!(el instanceof HTMLElement)) continue;
      if (own && (el === own || el.contains(own) || own.contains(el))) continue;
      const position = getComputedStyle(el).position;
      if (position === 'fixed' || position === 'sticky') out.push(el);
    }
    return out;
  }

  private contentRight(pinned: readonly HTMLElement[]): number {
    const own = this.ownRoot?.() ?? null;
    let max = 0;
    const consider = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      max = Math.max(max, rect.right);
    };

    for (const el of Array.from(document.body?.children ?? [])) {
      if (!(el instanceof HTMLElement)) continue;
      if (own && (el === own || el.contains(own))) continue;
      consider(el);
    }
    for (const el of pinned) consider(el);
    return max;
  }

  private clearsPanel(width: number, pinned: readonly HTMLElement[]): boolean {
    return this.contentRight(pinned) <= window.innerWidth - width + 24;
  }

  private saveProps(target: HTMLElement): void {
    this.saved.clear();
    for (const prop of PROPS) this.saved.set(prop, target.style.getPropertyValue(prop));
  }

  private restoreProps(target: HTMLElement): void {
    for (const prop of PROPS) {
      const original = this.saved.get(prop);
      if (original) target.style.setProperty(prop, original);
      else target.style.removeProperty(prop);
    }
  }

  /**
   * A client-rendered host has an empty root when the panel is restored on load,
   * so the first measurement says the content already clears the panel and
   * nothing is applied. The framework then renders at full width, under the
   * panel. Re-check when the host's size changes; the re-apply is a no-op once
   * the content does clear, so this cannot loop.
   */
  private watchContent(): void {
    if (this.contentObserver || typeof ResizeObserver !== 'function' || !document.body) return;
    this.contentObserver = new ResizeObserver(() => {
      if (!this.last.open || this.recheckRaf) return;
      this.recheckRaf = requestAnimationFrame(() => {
        this.recheckRaf = 0;
        if (!this.last.open) return;
        if (this.clearsPanel(this.last.width, this.collectPinned())) return;
        this.apply(true, this.last.width);
      });
    });
    this.contentObserver.observe(document.body);
  }

  apply(open: boolean, width: number): LayoutMode {
    this.last = { open, width };
    if (open) this.watchContent();
    const mode = this.resolveMode(width);
    this.effective = mode;

    if (!open || mode === 'overlay') {
      this.release();
      this.notify();
      return mode;
    }

    this.release();
    const pinned = this.collectPinned();

    if (this.clearsPanel(width, pinned)) {
      this.notify();
      return 'push';
    }

    for (const target of this.candidates()) {
      for (const strategy of STRATEGIES) {
        this.saveProps(target);
        strategy.apply(target, width);

        const own = this.ownRoot?.() ?? null;
        if (!own || !target.contains(own)) {
          target.style.setProperty('transform', 'translateZ(0)');
        }

        if (!this.clearsPanel(width, pinned)) {
          this.restoreProps(target);
          continue;
        }

        this.target = target;
        this.applied = true;
        target.style.setProperty('overflow-x', 'hidden');

        log.info(
          `push: ${strategy.name} on ${target === document.documentElement ? '<html>' : (target.id && `#${target.id}`) || target.tagName.toLowerCase()}`,
        );
        this.notify();
        return 'push';
      }
    }

    const wrap = this.createWrapper();
    if (wrap) {
      this.saveProps(wrap);
      wrap.style.setProperty('width', `calc(100% - ${width}px)`, 'important');
      wrap.style.setProperty('max-width', `calc(100% - ${width}px)`, 'important');
      wrap.style.setProperty('overflow-x', 'hidden');
      wrap.style.setProperty('transform', 'translateZ(0)');

      if (this.clearsPanel(width, pinned)) {
        this.target = wrap;
        this.applied = true;
        log.info('push: wrapper around the application');
        this.notify();
        return 'push';
      }
      this.removeWrapper();
    }

    log.warn('could not shrink the host application; using overlay mode');
    this.effective = 'overlay';
    this.notify();
    return 'overlay';
  }

  private createWrapper(): HTMLElement | null {
    const body = document.body;
    if (!body) return null;
    if (this.wrapper?.isConnected) return this.wrapper;

    const own = this.ownRoot?.() ?? null;
    const moving = Array.from(body.childNodes).filter((node) => node !== own);
    if (moving.length === 0) return null;

    const wrap = document.createElement('div');
    wrap.setAttribute(WRAP_ATTR, '');
    body.insertBefore(wrap, own ?? null);
    for (const node of moving) wrap.appendChild(node);

    this.wrapper = wrap;
    return wrap;
  }

  private removeWrapper(): void {
    const wrap = this.wrapper;
    if (!wrap) return;
    const body = document.body;
    if (body && wrap.isConnected) {
      for (const node of Array.from(wrap.childNodes)) body.insertBefore(node, wrap);
      wrap.remove();
    }
    this.wrapper = null;
  }

  private release(): void {
    if (this.target && this.applied) this.restoreProps(this.target);
    this.saved.clear();
    this.applied = false;
    this.target = null;
    this.removeWrapper();
  }

  private notify(): void {
    setTimeout(() => {
      this.selfDispatched = true;
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(
        new CustomEvent('agent-sdk:layout', {
          detail: { mode: this.effective, width: this.last.width, open: this.last.open },
        }),
      );
      this.selfDispatched = false;
    }, 0);
  }

  private onViewportResize = (): void => {
    if (this.selfDispatched || this.resizeRaf) return;
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = 0;
      if (this.last.open) this.apply(true, this.last.width);
    });
  };

  get mode(): LayoutMode {
    return this.effective;
  }

  destroy(): void {
    this.contentObserver?.disconnect();
    this.contentObserver = null;
    if (this.recheckRaf) cancelAnimationFrame(this.recheckRaf);
    window.removeEventListener('resize', this.onViewportResize);
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    this.release();
  }
}
